import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Warning, X } from "@phosphor-icons/react";

/**
 * v0.4.40 B7 — Amber banner when the calc period is a strict subset
 * of the uploaded data range. Informational only — does NOT block
 * filing (data spans the period; intent confirmation is the value).
 *
 * Dismissible per-calc via localStorage keyed on calc.id.
 */
export const PeriodCoverageBanner = ({ calc }) => {
  const { t } = useTranslation();
  const cov = calc?.period_coverage;
  const key = `vat.period_coverage.dismissed.${calc?.id || ""}`;
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && window.localStorage?.getItem(key) === "1"
  );
  if (!cov?.has_outside_rows || dismissed) return null;

  const onDismiss = () => {
    setDismissed(true);
    try { window.localStorage?.setItem(key, "1"); } catch (e) { /* ignore */ }
  };

  return (
    <div
      data-testid="period-coverage-banner"
      className="bg-amber-50 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 border border-amber-200 dark:border-amber-800 rounded-xl"
    >
      <div className="px-4 py-3 flex items-start gap-3">
        <Warning size={20} weight="bold" className="shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
        <div className="flex-1 space-y-1">
          <div className="text-[11px] tracking-normal tabular-nums text-amber-700 dark:text-amber-300">
            {t("ui.period_coverage.title", "Period coverage")}
          </div>
          <div data-testid="period-coverage-message" className="tabular-nums text-sm">
            {t("ui.period_coverage.message",
               "This calc covers {{calc_start}} → {{calc_end}}. Uploaded data also covers {{data_start}} → {{data_end}} ({{outside}} rows outside the period). Confirm this is intentional.",
               {
                 calc_start: cov.calc_start, calc_end: cov.calc_end,
                 data_start: cov.data_start, data_end: cov.data_end,
                 outside: (cov.rows_outside_period || 0).toLocaleString(),
               })}
          </div>
        </div>
        <button
          type="button"
          data-testid="period-coverage-dismiss"
          onClick={onDismiss}
          className="shrink-0 text-amber-700 dark:text-amber-300 hover:text-amber-100 border border-amber-200 dark:border-amber-800 hover:border-amber-300 rounded-lg px-2 py-1 tabular-nums text-[10px] tracking-normal flex items-center gap-1"
        >
          <X size={11} weight="bold" />
          {t("ui.period_coverage.dismiss", "Dismiss")}
        </button>
      </div>
    </div>
  );
};
